export class CancellablePromise<T> {
    abortController: AbortController;
    result: Promise<T>;

    constructor(makeRequest: (signal: AbortSignal) => Promise<T>) {
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        this.result = makeRequest(this.abortController.signal).then((v) => {
            if (signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
            return v;
        });
    }

    static From<T>(prom: Promise<T>): CancellablePromise<T> {
        return new CancellablePromise(() => prom);
    }

    cancel() {
        return this.abortController.abort();
    }
}

/** Resolves after `ms`, or rejects with an `AbortError` as soon as `signal` aborts. */
export function delay(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const abortError = () => new DOMException('Delay cancelled', 'AbortError');
        if (signal.aborted) {
            reject(abortError());
            return;
        }
        const onAbort = () => {
            clearTimeout(timer);
            reject(abortError());
        };
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal.addEventListener('abort', onAbort, { once: true });
    });
}

/**
 * Runs `makeRequest` with a signal that aborts when `signal` does, or with a `TimeoutError` once
 * `timeoutMs` elapses. The returned promise settles on abort only if `makeRequest` honours its signal.
 */
export async function withTimeout<T>(
    makeRequest: (signal: AbortSignal) => Promise<T>,
    timeoutMs: number,
    signal: AbortSignal
): Promise<T> {
    const controller = new AbortController();
    const onAbort = () => controller.abort(signal.reason);
    if (signal.aborted) onAbort();
    else signal.addEventListener('abort', onAbort, { once: true });
    const timer = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), timeoutMs);
    try {
        return await makeRequest(controller.signal);
    } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
    }
}

export class Deferred<T> {
    promise: Promise<T>;
    reject: (e: unknown) => unknown = () => {};
    resolve: (value: T) => unknown = () => {};
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.reject = reject;
            this.resolve = resolve;
        });
    }
}

export class HotSwappablePromise<T> {
    deferred: Deferred<T>;
    current?: CancellablePromise<T> = undefined;

    constructor(prom: CancellablePromise<T>) {
        this.deferred = new Deferred();

        this.swap(prom);
    }

    get result(): Promise<T> {
        return this.deferred.promise;
    }

    swap(newPromise: CancellablePromise<T>) {
        const oldPromise = this.current;

        this.current = newPromise;
        this.current.result.then(
            (result) => {
                if (this.current === newPromise) {
                    this.deferred.resolve(result);
                }
            },
            (error) => {
                if (this.current === newPromise) {
                    this.deferred.reject(error);
                }
            }
        );

        oldPromise?.cancel();
    }
}
